import { useState, useEffect } from "react";
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  ListItemIcon,
  Popover,
  Drawer,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ButtonsContainer, Notification } from "../utilities";
import { useDispatch } from "react-redux";
import Searchbar from "./Searchbar";
import { useLocation } from "react-router-dom";
import appLogo from "../../assets/images/app_logo.png";
import { useQuery } from "react-query";
import {
  MenuIcon,
  ShipmentIcon,
  PickupsIcon,
  ExportDataIcon,
  AdvancedSearch,
  TicketsIcon,
} from "../../assets/icons";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../helpers/AxiosInstance";
import { Link } from "react-router-dom";
import Translation from "../utilities/Translation";
import { handleLogout } from "../../redux/reducers/authReducer";
const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 520px;
  min-width: 300px;
  height: 480px;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  opacity: 1;
  @media (max-width: 1440px) {
    width: 400px;
  }
  @media (max-width: 425px) {
    min-width: 300px;
  }
`;

const StyledDrawer = styled(Drawer)`
  & .MuiDrawer-paper {
    width: 280px;
    background: linear-gradient(135deg, #324056 0%, #2a3447 100%);
    border: none;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }
`;

const DrawerHeader = styled(Box)`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DrawerLogo = styled(Box)`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseButton = styled(IconButton)`
  color: white;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Line = styled.hr`
  border: 1px solid var(--primary-color);
`;

const Button = styled(Link)`
  min-width: 162px;
  height: 50px;
  background: transparent;
  display: grid;
  place-items: center;
  text-align: center;
  font-weight: 700;
  font-family: Cairo;
  font-size: 18px;
  letter-spacing: 0px;
  color: #010e3f;
  text-decoration: none;
  opacity: 1;
  @media (max-width: 1440px) {
    font-size: 18px;
  }
`;
function MobileLayout({ RoleID }) {
  const { pathname } = useLocation();
  const instance = axiosInstance();
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { data, refetch } = useQuery(
    "notifications",
    async () => {
      const { data } = await instance.get("/GetActiveNotifications");
      return data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000,
      cacheTime: 3000,
    },
  );
  const dispatch = useDispatch();
  const { t } = useTranslation("layout");
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const openNotification = Boolean(anchor);
  const handleNotificationClick = (event) => {
    setAnchor(event.currentTarget);
  };
  const { Logo } = useSelector((state) => ({
    Logo:
      state.Auth.companyData.Logo || appLogo,
  }));
  const handleLogoutClick = () => {
    setOpen(false);
    dispatch(handleLogout());
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  const isRTL = i18n.language === "ar";
  const drawerAnchor = isRTL ? "right" : "left";

  const drawerContent = (
    <>
      <DrawerHeader>
        <DrawerLogo>
          <Box
            component="img"
            src={Logo}
            sx={{
              width: "30px",
              height: "30px",
              borderRadius: "4px",
            }}
          />
        </DrawerLogo>
        <CloseButton onClick={() => setOpen(false)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </CloseButton>
      </DrawerHeader>
      <List
        sx={{
          width: "100%",
          padding: "10px 0",
          "& .MuiListItemButton-root": {
            color: "rgba(255, 255, 255, 0.8)",
            borderRadius: "8px",
            margin: "2px 12px",
            transition: "all 0.2s ease",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.1)",
              color: "white",
            },
            "&.active": {
              background: "rgba(255, 255, 255, 0.15)",
              color: "white",
            },
          },
        }}
        component="nav"
        aria-labelledby="nested-list-subheader"
      >
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            py: 1,
            marginBottom: 2,
            color: "white",
          }}
        >
          <ListItemIcon sx={{ minWidth: "35px" }}>
            <img src={MenuIcon} alt="" />
          </ListItemIcon>
          <ListItemText primary={t("Sidebar.Menu")} />
        </ListItemButton>
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/reports/shipments"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: "35px" }}>
            <img src={ShipmentIcon} alt="" />
          </ListItemIcon>
          <ListItemText primary={t("Sidebar.Shipments")} />
        </ListItemButton>
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/reports/pickups"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: "35px" }}>
            <img src={PickupsIcon} alt="" />
          </ListItemIcon>
          <ListItemText primary={t("Sidebar.Pickups")} />
        </ListItemButton>
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/reports/export"
          sx={{
            color: "white",
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "19px" : "12px",
            paddingLeft: isRTL ? "12px" : "19px",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: "35px" }}>
            <img src={ExportDataIcon} alt="" />
          </ListItemIcon>
          <ListItemText primary={t("Sidebar.Reports_ExportData")} />
        </ListItemButton>
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/multi-search"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "12px" : "12px",
            py: 2,
          }}
        >
          <ListItemIcon>
            <img src={AdvancedSearch} alt="" />
          </ListItemIcon>
          <ListItemText
            sx={{ color: "white" }}
            primary={t("Sidebar.Advanced_Search")}
          />
        </ListItemButton>
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/new-shipment"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemText primary={t("Navbar.add_shipment")} />
        </ListItemButton>
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/new-pickup"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemText primary={t("Navbar.add_pickup")} />
        </ListItemButton>
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/tickets"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemText primary={t("Sidebar.Tickets")} />
        </ListItemButton>
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/new-request"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemIcon sx={{ minWidth: "35px" }}>
            <img src={TicketsIcon} alt="" />
          </ListItemIcon>
          <ListItemText primary={t("Sidebar.New_Request")} />
        </ListItemButton>
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/quick-newshipment"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemText primary={t("Sidebar.Quick_NewShipment")} />
        </ListItemButton>
        {/* DataAnalysis and AiAssistant removed from mobile menu per request */}
        <ListItemButton
          onClick={() => setOpen(false)}
          component={NavLink}
          to="/upload"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemText primary={t("Navbar.upload_excel")} />
        </ListItemButton>
        <ListItemButton
          component={NavLink}
          to="/change-password"
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemText primary={t("Navbar.change_password")} />
        </ListItemButton>
        <ListItemButton
          onClick={() => handleLogoutClick()}
          sx={{
            textAlign: isRTL ? "right" : "left",
            paddingRight: isRTL ? "30px" : "12px",
            paddingLeft: isRTL ? "12px" : "30px",
            color: "white",
            gap: "2px",
            py: 2,
          }}
        >
          <ListItemText primary={t("Navbar.signout")} />
        </ListItemButton>
      </List>
    </>
  );
  return (
    <>
      {/* Prevent body scroll when drawer is open on mobile */}
      {isMobile && open && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            backdropFilter: "blur(4px)",
            zIndex: 1200,
          }}
          onClick={() => setOpen(false)}
        />
      )}

      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 243,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "var(--gradient)",
            borderBottom: "2px solid #EEE",
          }}
        >
          {RoleID === 2 && (
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                width: 48,
                height: 48,
                cursor: "pointer",
              }}
              onClick={() => setOpen(!open)}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            {RoleID === 1 && (
              <Box
                onClick={handleLogoutClick}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "7px",
                }}
              >
                <Box
                  sx={{
                    height: "30px",
                    width: "30px",
                    position: "relative",
                    marginInlineStart: "5px",
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path
                      fill="#FFF"
                      d="M160 96c17.7 0 32-14.3 32-32s-14.3-32-32-32H96C43 32 0 75 0 128V384c0 53 43 96 96 96h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H96c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32h64zM504.5 273.4c4.8-4.5 7.5-10.8 7.5-17.4s-2.7-12.9-7.5-17.4l-144-136c-7-6.6-17.2-8.4-26-4.6s-14.5 12.5-14.5 22v72H192c-17.7 0-32 14.3-32 32l0 64c0 17.7 14.3 32 32 32H320v72c0 9.6 5.7 18.2 14.5 22s19 2 26-4.6l144-136z"
                    />
                  </svg>
                </Box>
              </Box>
            )}

            {RoleID === 2 && (
              <>
                <IconButton
                  sx={{
                    background: "transparent",
                    position: "relative",
                    "&::before": {
                      content: "''",
                      position: "absolute",
                      top: "0",
                      right: "0",
                      width: "15px",
                      borderRadius: "50%",
                      height: "15px",
                      backgroundColor: data?.notifications.length
                        ? "white"
                        : "transparent",
                    },
                  }}
                  onClick={handleNotificationClick}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 29.255 33"
                  >
                    <path
                      d="M32.288,23.088,29.25,20.015V14.78A11.412,11.412,0,0,0,19.125,3.4V1.125h-2.25V3.4A11.5,11.5,0,0,0,6.75,14.78v5.234L3.712,23.088a1.041,1.041,0,0,0-.337.8V27.3a1.076,1.076,0,0,0,.307.827,1.05,1.05,0,0,0,.818.311h7.875a5.625,5.625,0,1,0,11.25,0H31.5a1.05,1.05,0,0,0,.818-.311,1.076,1.076,0,0,0,.307-.827V23.884A1.041,1.041,0,0,0,32.288,23.088ZM18,31.849a3.4,3.4,0,0,1-3.375-3.414h6.75A3.4,3.4,0,0,1,18,31.849Z"
                      transform="translate(-3.373 -1.125)"
                      fill="white"
                    />
                  </svg>
                </IconButton>
              </>
            )}
            <Translation isMobile />
            <Box>{!pathname.includes("cshome") && <Searchbar />}</Box>
          </Box>
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              border: "3px solid #e2e8f0",
              padding: "3px",
              background: "#dcdcdc",
            }}
          >
            <Box
              component="img"
              src={Logo}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "50%",
                "@media (max-width: 330px)": {
                  display: "none",
                },
              }}
            />
          </Box>
        </Box>

        {/* Mobile Drawer */}
        <StyledDrawer
          anchor={drawerAnchor}
          open={open && isMobile}
          onClose={() => setOpen(false)}
          variant="temporary"
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          {drawerContent}
        </StyledDrawer>

        {/* Desktop Collapse */}
        {!isMobile && (
          <>
            <Collapse in={open} sx={{ background: "var(--gradient)" }}>
              <List
                sx={{ width: "100%" }}
                component="nav"
                aria-labelledby="nested-list-subheader"
              >
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/"
                  sx={{
                    textAlign: "right",
                    paddingRight: "30px",
                    py: 1,
                    marginBottom: 2,
                    color: "white",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "35px" }}>
                    <img src={MenuIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText primary={t("Sidebar.Menu")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/reports/shipments"
                  sx={{
                    textAlign: "right",
                    paddingRight: "30px",
                    color: "white",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "35px" }}>
                    <img src={ShipmentIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText primary={t("Sidebar.Shipments")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/reports/pickups"
                  sx={{
                    textAlign: "right",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                    color: "white",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "35px" }}>
                    <img src={PickupsIcon} alt="" />
                  </ListItemIcon>

                  <ListItemText primary={t("Sidebar.Pickups")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/reports/export"
                  sx={{
                    color: "white",
                    textAlign: "right",
                    paddingRight: "19px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "35px" }}>
                    <img src={ExportDataIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText primary={t("Sidebar.Reports_ExportData")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/multi-search"
                  sx={{
                    textAlign: "right",
                    paddingRight: "12px",
                    py: 2,
                  }}
                >
                  <ListItemIcon>
                    <img src={AdvancedSearch} alt="" />
                  </ListItemIcon>
                  <ListItemText
                    sx={{ color: "white" }}
                    primary={t("Sidebar.Advanced_Search")}
                  />
                </ListItemButton>

                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/new-shipment"
                  sx={{
                    textAlign: "right",
                    paddingRight: "30px",
                    paddingLeft: "12px",
                    color: "white",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemText primary={t("Navbar.add_shipment")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/new-pickup"
                  sx={{
                    textAlign: "right",
                    color: "white",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemText primary={t("Navbar.add_pickup")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/tickets"
                  sx={{
                    textAlign: "right",
                    color: "white",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemText primary={t("Sidebar.Tickets")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/new-request"
                  sx={{
                    textAlign: "right",
                    color: "white",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: "35px" }}>
                    <img src={TicketsIcon} alt="" />
                  </ListItemIcon>
                  <ListItemText primary={t("Sidebar.New_Request")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/quick-newshipment"
                  sx={{
                    textAlign: "right",
                    color: "white",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemText primary={t("Sidebar.Quick_NewShipment")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/data-analysis"
                  sx={{
                    textAlign: "right",
                    color: "white",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemText primary={t("Sidebar.data_analysis")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/ai-assistant"
                  sx={{
                    textAlign: "right",
                    color: "white",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemText primary={t("Sidebar.ai_assistant")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => setOpen(false)}
                  component={NavLink}
                  to="/upload"
                  sx={{
                    textAlign: "right",
                    color: "white",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemText primary={t("Navbar.upload_excel")} />
                </ListItemButton>
                <ListItemButton
                  component={NavLink}
                  to="/change-password"
                  sx={{
                    textAlign: "right",
                    color: "white",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemText primary={t("Navbar.change_password")} />
                </ListItemButton>
                <ListItemButton
                  onClick={() => handleLogoutClick()}
                  sx={{
                    textAlign: "right",
                    color: "white",
                    paddingRight: "30px",
                    gap: "2px",
                    py: 2,
                  }}
                >
                  <ListItemText primary={t("Navbar.signout")} />
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        <Box>
          <Popover
            open={openNotification}
            anchorEl={anchor}
            onClick={() => {
              refetch();
              setAnchor(null);
            }}
            onClose={() => setAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            sx={{ fontFamily: "Cairo" }}
          >
            <Card>
              <div dir="rtl">
                {data
                  ? data.notifications
                      .slice(0, 3)
                      .map((notification, index) => (
                        <Notification
                          key={index}
                          AWB={notification.AWB}
                          Body={notification.Body}
                          Status={notification.Title}
                          Serial={notification.Serial}
                        />
                      ))
                  : null}
                <Line />
                <ButtonsContainer
                  style={{ marginTop: "10px", marginBottom: "10px" }}
                >
                  <Button to="/notifications" onClick={() => setAnchor(null)}>
                    {t("Navbar.all_notifications")}
                  </Button>
                </ButtonsContainer>
              </div>
            </Card>
          </Popover>
        </Box>
      </Box>
    </>
  );
}
export default MobileLayout;
